import { useEffect, useMemo, useRef, useState } from "react";
import { FileDown, FileUp, SlidersHorizontal, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button.jsx";
import { TableCell } from "@/components/ui/table.jsx";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip.jsx";
import ClassificationGridShell from "@/components/downtime-classification/classification-grid-shell.jsx";
import {
  buildClassificationsToSave,
  buildPendingFromCsvRows,
  classificationErrorDetail,
  getClassificationRowKey,
  mergePendingClassifications,
  parseClassificationCsv,
} from "@/components/downtime-classification/downtime-classification-utils.js";
import { useClassificationGrid } from "@/hooks/useClassificationGrid.js";
import { saveDowntimeClassification } from "@/api/downtime-classification-api.js";
import { shouldReportApiError } from "@/api/api-utils.js";
import { useError } from "@/contexts/ErrorContext.jsx";
import { downloadUnmappedClassificationsCSV } from "@/lib/csv-utils.js";
import { formatNumber } from "@/lib/formatting-utils.js";
import { cn } from "@/lib/utils.js";
import { outlineChromeButton } from "@/lib/status-styles.js";

/** @type {import("@/components/downtime-classification/classification-grid-shell.jsx").ClassificationColumn[]} */
const COLUMNS = [
  { key: "Vendor", label: "Vendor", vendorFilter: true },
  { key: "Message", label: "Description" },
  { key: "DowntimeEventCount", label: "Events", alignRight: true },
  { key: "DowntimeHoursCount", label: "Hours", alignRight: true },
  { key: "IsMech", label: "Classify As" },
];

const ACTION_BUTTON_CLASS = cn(
  outlineChromeButton,
  "disabled:opacity-50 disabled:cursor-not-allowed"
);

/**
 * Outline action button with a hover tooltip (works while disabled via span wrapper).
 * @param {Object} props
 * @param {boolean} props.disabled
 * @param {() => void} props.onClick
 * @param {import("react").ReactNode} props.children
 * @param {import("react").ReactNode} props.tooltip
 * @returns {JSX.Element}
 */
function CsvActionButton({ disabled, onClick, children, tooltip }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className='inline-flex'>
          <Button
            type='button'
            variant='outline'
            size='sm'
            disabled={disabled}
            onClick={onClick}
            className={ACTION_BUTTON_CLASS}>
            {children}
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent side='bottom' sideOffset={6} className='max-w-xs'>
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Grid of unmapped downtime reasons awaiting Mech / Non-Mech classification.
 * Supports search, vendor column filter, column sorting, CSV bulk import/export, and saving.
 * @param {Object} props
 * @param {import("@/api/downtime-classification-api.js").UnmappedClassifications[]} props.rows
 * @param {boolean} props.isLoading
 * @param {string|null} props.error
 * @param {string[]|null} [props.selectedVendors] - Controlled vendor filter (`null` = all)
 * @param {(vendors: string[]|null) => void} [props.onSelectedVendorsChange]
 * @param {() => Promise<void>} [props.onSaveSuccess] - Called after a successful save (e.g. refetch lists)
 * @returns {JSX.Element}
 */
export default function NeedsMappingTable({
  rows,
  isLoading,
  error,
  selectedVendors: controlledSelectedVendors,
  onSelectedVendorsChange,
  onSaveSuccess,
}) {
  const { addError } = useError();
  /** @type {[Record<string, boolean>, Function]} */
  const [pendingClassifications, setPendingClassifications] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef(/** @type {HTMLInputElement|null} */ (null));

  // Drop pending selections for rows that no longer exist (e.g. after refetch)
  useEffect(() => {
    const rowKeys = new Set(rows.map((row) => getClassificationRowKey(row)));
    setPendingClassifications((prev) => {
      const next = /** @type {Record<string, boolean>} */ ({});
      let changed = false;
      for (const [key, value] of Object.entries(prev)) {
        if (rowKeys.has(key)) {
          next[key] = value;
        } else {
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [rows]);

  const rowsWithPending = useMemo(
    () => mergePendingClassifications(rows, pendingClassifications),
    [rows, pendingClassifications]
  );

  const {
    search,
    setSearch,
    selectedVendors,
    setSelectedVendors,
    sortColumn,
    sortDirection,
    handleSort,
    vendorOptions,
    filteredAndSortedRows,
  } = useClassificationGrid(rowsWithPending, {
    initialSortColumn: "DowntimeEventCount",
    initialSortDirection: "desc",
    selectedVendors: controlledSelectedVendors,
    onSelectedVendorsChange,
  });

  const changedCount = Object.keys(pendingClassifications).length;
  const hasChanges = changedCount > 0;
  const csvActionsDisabled = isLoading || rows.length === 0 || isSaving;

  /**
   * Sets or clears a pending Mech / Non-Mech classification for a row.
   * Clicking the already-selected value clears the assignment.
   * @param {{ Vendor?: string, Message?: string }} row
   * @param {boolean} isMech
   */
  const handleClassify = (row, isMech) => {
    const key = getClassificationRowKey(row);
    setPendingClassifications((prev) => {
      const next = { ...prev };
      if (next[key] === isMech) {
        delete next[key];
      } else {
        next[key] = isMech;
      }
      return next;
    });
  };

  const handleSave = async () => {
    const classifications = buildClassificationsToSave(
      rows,
      pendingClassifications
    );
    if (classifications.length === 0) return;

    setIsSaving(true);
    try {
      await saveDowntimeClassification(classifications);
      setPendingClassifications({});
      if (onSaveSuccess) {
        await onSaveSuccess();
      }
    } catch (err) {
      if (shouldReportApiError(err)) {
        addError(
          "Failed to save downtime classifications.",
          classificationErrorDetail(err)
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Applies CSV IsMech values as pending classifications.
   * @param {import('react').ChangeEvent<HTMLInputElement>} event
   */
  const handleCsvFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    try {
      const text = await file.text();
      const fromCsv = buildPendingFromCsvRows(
        rows,
        parseClassificationCsv(text)
      );
      const appliedCount = Object.keys(fromCsv).length;

      if (appliedCount === 0) {
        addError(
          "No classifications applied from CSV.",
          "No rows matched unmapped Vendor/Message pairs with a valid IsMech value (Mech, Non-Mech, true/false, 1/0)."
        );
        return;
      }

      setPendingClassifications((prev) => ({ ...prev, ...fromCsv }));
    } catch (err) {
      addError(
        "Failed to import classification CSV.",
        classificationErrorDetail(err)
      );
    }
  };

  return (
    <ClassificationGridShell
      search={search}
      onSearchChange={setSearch}
      searchTestId='input-search-needs-mapping'
      headerActions={
        <div className='flex items-center gap-2 shrink-0'>
          <input
            ref={fileInputRef}
            type='file'
            accept='.csv,text/csv'
            className='hidden'
            onChange={handleCsvFileChange}
          />
          <CsvActionButton
            disabled={csvActionsDisabled}
            onClick={() => downloadUnmappedClassificationsCSV(rowsWithPending)}
            tooltip={
              <>
                <p className='font-medium mb-1'>Download unmapped reasons</p>
                <p>
                  Exports Vendor, Message, event/hour counts, and an{" "}
                  <span className='font-medium'>IsMech</span> column. Fill
                  IsMech with <span className='font-medium'>Mech</span> or{" "}
                  <span className='font-medium'>Non-Mech</span> (also
                  true/false, 1/0, or yes/no). Leave blank to skip a row.
                </p>
              </>
            }>
            <FileDown className='h-4 w-4 mr-1' />
            Download CSV
          </CsvActionButton>
          <CsvActionButton
            disabled={csvActionsDisabled}
            onClick={() => fileInputRef.current?.click()}
            tooltip={
              <p>
                Upload the downloaded CSV with IsMech filled in to apply those
                classifications in the UI. Then click Save Mappings to persist.
              </p>
            }>
            <FileUp className='h-4 w-4 mr-1' />
            Upload CSV
          </CsvActionButton>
          <Button
            type='button'
            variant='outline'
            size='sm'
            disabled={!hasChanges || isSaving || isLoading}
            onClick={handleSave}
            className={ACTION_BUTTON_CLASS}>
            {isSaving
              ? "Saving..."
              : hasChanges
                ? `Save Mappings (${changedCount})`
                : "Save Mappings"}
          </Button>
        </div>
      }
      isLoading={isLoading}
      loadingMessage='Loading unmapped 3rd-party downtime reasons...'
      error={error}
      isEmpty={rows.length === 0}
      emptyMessage='No unmapped 3rd-party downtime reasons found'
      columns={COLUMNS}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      onSort={handleSort}
      vendorOptions={vendorOptions}
      selectedVendors={selectedVendors}
      onVendorsChange={setSelectedVendors}
      filteredAndSortedRows={filteredAndSortedRows}
      getRowKey={(row) => getClassificationRowKey(row)}
      renderRow={(row) => {
        const isMech = row.IsMech;
        return (
          <>
            <TableCell className='text-sm text-foreground font-normal py-3 px-4'>
              {row.Vendor}
            </TableCell>
            <TableCell className='text-sm text-foreground font-normal py-3 px-4'>
              {row.Message}
            </TableCell>
            <TableCell className='text-sm text-foreground font-normal py-3 px-4 text-right'>
              <span className='text-sm font-mono font-light'>
                {formatNumber(row.DowntimeEventCount, 0)}
              </span>
            </TableCell>
            <TableCell className='text-sm text-foreground font-normal py-3 px-4 text-right'>
              <span className='text-sm font-mono font-light'>
                {formatNumber(row.DowntimeHoursCount, 2)}
              </span>
            </TableCell>
            <TableCell className='text-sm text-foreground font-normal py-3 px-4'>
              <div className='flex items-center gap-2'>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  disabled={isSaving}
                  onClick={() => handleClassify(row, true)}
                  className={cn(
                    "border-red-500 text-red-600 hover:bg-red-50 hover:text-red-700",
                    isMech === true
                      && "bg-red-500 text-white hover:bg-red-600 hover:text-white"
                  )}>
                  <Wrench className='h-4 w-4' />
                  Mech
                </Button>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  disabled={isSaving}
                  onClick={() => handleClassify(row, false)}
                  className={cn(
                    "border-blue-500 text-blue-600 hover:bg-blue-50 hover:text-blue-700",
                    isMech === false
                      && "bg-blue-500 text-white hover:bg-blue-600 hover:text-white"
                  )}>
                  <SlidersHorizontal className='h-4 w-4' />
                  Non-Mech
                </Button>
              </div>
            </TableCell>
          </>
        );
      }}
    />
  );
}
